import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import {
  useGetTenantsQuery,
  useOnboardTenantMutation,
  useToggleTenantStatusMutation
} from '../../../store/api/saasApi';

function TenantManagement() {
  const { data: tenants, isLoading } = useGetTenantsQuery();
  const [onboardTenant] = useOnboardTenantMutation();
  const [toggleStatus] = useToggleTenantStatusMutation();

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    companyCode: '',
    adminUsername: '',
    adminPassword: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
  });
  const [error, setError] = useState(null);

  const handleToggle = async (id, currentStatus) => {
    try {
      await toggleStatus({ id, isActive: !currentStatus }).unwrap();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      await onboardTenant(formData).unwrap();
      setOpen(false);
      setFormData({
        companyName: '',
        companyCode: '',
        adminUsername: '',
        adminPassword: '',
        adminFirstName: '',
        adminLastName: '',
        adminEmail: '',
      });
    } catch (err) {
      setError(err?.data?.message || 'Failed to onboard tenant');
    }
  };

  if (isLoading) return <Typography>Loading tenants...</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Tenant Management (SaaS)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Add New Tenancy
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FA' }}>
              <TableCell sx={{ fontWeight: 700 }}>Company Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Admin User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants?.map((tenant) => (
              <TableRow key={tenant.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BusinessIcon color="primary" />
                    <Typography sx={{ fontWeight: 600 }}>{tenant.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={tenant.code} size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
                <TableCell>
                  {tenant.users?.[0]?.username || 'N/A'}
                </TableCell>
                <TableCell>
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={tenant.isActive ? 'Active' : 'Inactive'}
                    color={tenant.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={tenant.isActive}
                    onChange={() => handleToggle(tenant.id, tenant.isActive)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Onboarding Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Onboard New Tenancy</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company Name"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company Short Code (e.g. NTL)"
                required
                value={formData.companyCode}
                onChange={(e) => setFormData({ ...formData, companyCode: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 12 }}><Typography variant="subtitle2" color="primary">Initial Admin Account</Typography></Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Admin Username"
                required
                value={formData.formDataadminUsername}
                onChange={(e) => setFormData({ ...formData, adminUsername: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Admin Password"
                type="password"
                required
                value={formData.adminPassword}
                onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.adminFirstName}
                onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.adminLastName}
                onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Email"
                value={formData.adminEmail}
                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>Create Tenancy</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenantManagement;
